/**
 * Product Requests API Client
 *
 * Endpoints for the admin product-request workflow:
 *   - Fetch pending requests (inbox)
 *   - Fetch single request detail
 *   - Approve / Reject a request
 */
import type { AxiosResponse } from 'axios';
import { getApiClient } from '@/core/services/api.service';
import { API_ROUTES } from '@/core/config';
import type {
  ProductRequestListItem,
  ProductRequestDynamoItem,
  ActionProductRequest,
} from '@tms-phoenix/tms-types';

const API_PREFIX = API_ROUTES.PRODUCTS;
const api = getApiClient();

// ---------------------------------------------------------------------------
// Pending inbox
// ---------------------------------------------------------------------------

/**
 * GET /products/api/v1/product-requests/admin/pending
 *
 * The backend returns { success, data: ProductRequestListItem[], meta: { cursor } }.
 * createApiClient unwraps the envelope, so response.data is the array directly.
 */
export function fetchPendingRequests(params?: {
  limit?: number;
  cursor?: string;
}): Promise<AxiosResponse<ProductRequestListItem[]>> {
  return api.get<ProductRequestListItem[]>(`${API_PREFIX}/product-requests/admin/pending`, { params });
}

/**
 * GET /products/api/v1/product-requests/admin/pending-count
 *
 * Returns the total count of pending requests for the badge.
 */
export function fetchPendingCount(): Promise<AxiosResponse<{ count: number }>> {
  return api.get<{ count: number }>(`${API_PREFIX}/product-requests/admin/pending-count`);
}

// ---------------------------------------------------------------------------
// Single request detail
// ---------------------------------------------------------------------------

/**
 * GET /products/api/v1/product-requests/:requestId
 *
 * Full product request detail.
 */
export function fetchProductRequestDetail(
  requestId: string,
): Promise<AxiosResponse<ProductRequestDynamoItem>> {
  return api.get<ProductRequestDynamoItem>(`${API_PREFIX}/product-requests/${requestId}`);
}

// ---------------------------------------------------------------------------
// Approve / Reject
// ---------------------------------------------------------------------------

/**
 * POST /products/api/v1/product-requests/:requestId/action
 *
 * Approve or reject a pending product request.
 */
export function actionProductRequest(
  requestId: string,
  body: ActionProductRequest,
): Promise<AxiosResponse<ProductRequestDynamoItem>> {
  return api.post<ProductRequestDynamoItem>(`${API_PREFIX}/product-requests/${requestId}/action`, body);
}
